import * as Sentry from "@sentry/browser";
import { FirestoreError } from "firebase/firestore";

/**
 * Membership-migration telemetry.
 *
 * This is a plain module rather than a hook, so the same counters are
 * reachable from the SDK-consuming providers, from the permission resolver,
 * and from the watcher stores. Those three do not share a React subtree.
 *
 * The client already initializes Sentry (`index.tsx`) with
 * `tracesSampleRate: 0.2`. That setting does not sample `captureMessage`
 * calls.
 */

type MembershipTelemetryContext = {
  deployEnv: string;
  inMobileWebView: boolean;
  platform: "web";
  /** True when this session reads member documents. The `flagName`
   *  dimension. */
  membersV2Enabled: boolean;
};

let telemetryContext: MembershipTelemetryContext = {
  /* `Sentry.init`'s `environment` is `process.env.NODE_ENV`, and that is
   * "production" for every Netlify context, deploy previews included,
   * because the build runs under NODE_ENV=production. So the deploy
   * environment must be an explicit tag here; Sentry's own `environment`
   * field cannot stand in for it. */
  deployEnv: process.env.DEPLOY_ENV || "local",
  inMobileWebView: false,
  platform: "web",
  membersV2Enabled: false,
};

/**
 * `MembershipFlagsContextProvider` calls this whenever the resolved mode or
 * the WebView signal changes. A repeat call with the same values changes
 * nothing, and no call causes a re-render.
 */
export const setMembershipTelemetryContext = (
  next: Partial<MembershipTelemetryContext>,
) => {
  telemetryContext = { ...telemetryContext, ...next };
};

/**
 * The dimension set that every membership event carries.
 *
 * In the six-flag version, `flagName` was the comma-joined set of the active
 * client flags, because a single listener can be open on behalf of two of
 * them. Only the ramp procedure kept the value single in practice. There
 * is one server-side mode now, so the dimension is single by construction and
 * the comma cannot appear. The tag names are unchanged on purpose: the
 * existing Sentry searches and alerts still match, and only the set of values
 * gets smaller.
 */
const baseTags = (extra?: {
  spaceId?: string;
  errorCode?: string;
  resolverSource?: "member-doc" | "legacy-fallback" | "absent";
}) => ({
  "membership.flagName": telemetryContext.membersV2Enabled
    ? "members_v2_new"
    : "none",
  "membership.flagValue": String(telemetryContext.membersV2Enabled),
  "membership.deployEnv": telemetryContext.deployEnv,
  "membership.inMobileWebView": String(telemetryContext.inMobileWebView),
  "membership.platform": telemetryContext.platform,
  ...(extra?.spaceId ? { "membership.spaceId": extra.spaceId } : {}),
  ...(extra?.errorCode ? { "firestore.code": extra.errorCode } : {}),
  ...(extra?.resolverSource
    ? { "membership.resolverSource": extra.resolverSource }
    : {}),
});

/**
 * The denominator for "errors per 1,000 `members-v1` listener attachments".
 * One event per attachment, so the alert is a ratio between two numbers this
 * module emits. Every place that opens a `members-v1` listener calls this.
 *
 * `level: "info"` is a volume decision, not a free one. If the Sentry quota
 * cannot absorb one event per attachment, the denominator moves to one event
 * per session that carries the attachment count. But the denominator must
 * exist somewhere, because a raw error count is not the alert we need.
 */
export const reportMembershipAttach = (
  op: "my-members" | "my-roots" | "roster" | "access",
) => {
  Sentry.captureMessage("membership.listener_attached", {
    level: "info",
    tags: { "membership.op": op, ...baseTags() },
  });
};

/**
 * A membership listener failed. A collection-group `list` fails as a whole,
 * with no partial results, so a single denial empties a whole surface. This
 * event is the only signal we get: without it, a rules regression on web is
 * completely invisible, because `watchEntities.ts` only `console.log`s the
 * error.
 */
export const reportMembershipError = (
  op: "my-members" | "my-roots" | "share-key-members" | "roster" | "access",
  error: FirestoreError,
) => {
  Sentry.captureMessage("membership.listener_error", {
    level: "error",
    tags: {
      "membership.op": op,
      ...baseTags({ errorCode: error?.code }),
    },
  });
};

/**
 * The engine asked about a (space, identity) pair that the stream does not
 * cover, and it fell back to the legacy in-document role map. That is
 * harmless while the legacy fields are still the source of truth, and fatal
 * once the migration deletes them, because the fallback then becomes a deny.
 * So this counter is the gate on that deletion: it must be near zero in
 * production first.
 *
 * The report is limited to once per space per session. Render code calls it,
 * and without the limit the event volume in Sentry hides the one signal that
 * is the gate on the legacy-field deletion. The event also reports the size
 * of the Set. A small Set with a high event count means one hot space; a
 * large Set means the loss covers many spaces.
 */
const reportedMisses = new Set<string>();

export const reportMembershipMiss = (spaceId: string) => {
  if (!spaceId || reportedMisses.has(spaceId)) return;
  reportedMisses.add(spaceId);
  Sentry.captureMessage("membership.resolver_miss", {
    level: "warning",
    tags: { ...baseTags({ spaceId, resolverSource: "legacy-fallback" }) },
    extra: { distinctSpaces: reportedMisses.size },
  });
};

/** A my-memberships stream hit its `limit`. Cooperative teardown is off for
 *  the rest of this session. */
const reportedSaturation = new Set<string>();

export const reportMembershipSaturated = (memberId: string) => {
  if (!memberId || reportedSaturation.has(memberId)) return;
  reportedSaturation.add(memberId);
  Sentry.captureMessage("membership.stream_saturated", {
    level: "warning",
    tags: { ...baseTags() },
  });
};

/**
 * A generic entity watcher failed. This instrument is not behind the flag:
 * before this, `useWatcherV2` and `useEntities` both swallowed the error
 * code, so a rules regression on the sidebar's folder-expansion query
 * produces nothing observable at all.
 *
 * Expect a step change in Sentry volume on the day this ships. Two of the
 * four call sites are unbounded retry loops on `permission-denied`: a denied
 * listener sends the same denied query again every 5 seconds for the life of
 * the tab. That volume is older than this instrument; the instrument only
 * makes it visible. Do not mute the tag before you measure the error-rate
 * baseline.
 */
export const reportListenerError = (
  collectionId: string,
  error?: FirestoreError,
) => {
  Sentry.captureMessage("firestore.listener_error", {
    level: error?.code === "permission-denied" ? "error" : "warning",
    tags: {
      "firestore.collection": collectionId,
      ...baseTags({ errorCode: error?.code }),
    },
  });
};

/**
 * A roster loaded fully but has fewer rows than `space.memberCount`.
 *
 * This is the only detector for a member doc without an `addedAt` field:
 * Firestore drops a document without the `orderBy` field from the result,
 * with no error at all.
 */
export const reportRosterShortfall = (
  spaceId: string,
  loaded: number,
  expected: number,
) => {
  Sentry.captureMessage("membership.roster_shortfall", {
    level: "error",
    tags: { ...baseTags({ spaceId }) },
    extra: { loaded, expected },
  });
};

/** The Share dialog cannot resolve an entity. To the user this presents as
 *  a share button that does nothing. */
export const reportShareDialogUnresolved = (spaceId: string) => {
  Sentry.captureMessage("membership.share_dialog_unresolved", {
    level: "error",
    tags: { ...baseTags({ spaceId }) },
  });
};
